import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import PrivacyModal from "./components/PrivacyModal";
import InstallPrompt from "./components/InstallPrompt";
import Landing from "./pages/Landing";
import Promociones from "./pages/Promociones";
import Login from "./pages/Login";
import AdminLogin from "./pages/AdminLogin";
import Dashboard from "./pages/Dashboard";
import Chat from "./pages/Chat";
import Tools from "./pages/Tools";
import Groups from "./pages/Groups";
import GroupJoin from "./pages/GroupJoin";
import SOSPage from "./pages/SOSPage";
import Admin from "./pages/Admin";
import AgencySettings from "./pages/AgencySettings";
import MasterAgencies from "./pages/MasterAgencies";
import MasterReports from "./pages/MasterReports";
import MasterPreferences from "./pages/MasterPreferences";
import MasterPromoCodes from "./pages/MasterPromoCodes";
import AgencyBilling from "./pages/AgencyBilling";
import Feedback from "./pages/Feedback";
import { Toaster, toast } from "sonner";
import { SettingsProvider } from "./lib/settings";
import { registerServiceWorkerIfWeb, onForegroundPush } from "./lib/native";
import { initTheme } from "./lib/theme";

export default function App() {
  useEffect(() => {
    initTheme();
  }, []);

  useEffect(() => {
    // Registra el Service Worker solo en web; en la app nativa (Capacitor)
    // las notificaciones locales se manejan con @capacitor/local-notifications.
    registerServiceWorkerIfWeb();

    // Pushes reales (FCM) que llegan mientras la app está abierta: se muestran
    // como un toast en pantalla (los que llegan con la app cerrada/en segundo
    // plano los maneja firebase-messaging-sw.js automáticamente).
    onForegroundPush((payload) => {
      const { title, body } = payload.notification || {};
      const mapsUrl = payload.data?.maps_url;
      // Fallback genérico, no de una agencia específica: este toast vive fuera
      // de <SettingsProvider> (useEffect de nivel de App), y el título real
      // casi siempre viene del payload del push de todos modos.
      toast(title || "Travel Shield", {
        description: body,
        duration: 10000,
        action: mapsUrl
          ? { label: "Ver ubicación", onClick: () => window.open(mapsUrl, "_blank") }
          : undefined,
      });
    });
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <SettingsProvider>
          <PrivacyModal />
          <InstallPrompt />
          <Toaster theme="dark" position="top-right" />
          <Routes>
            {/* Rutas de cliente: existen sin prefijo (agencia "default" / Cherry
                Travels) y otra vez bajo /a/:slug para cada agencia con su
                propia marca — mismos componentes, misma lógica. */}
            {["", "/a/:slug"].map((prefix) => (
              <React.Fragment key={prefix || "root"}>
                <Route path={`${prefix}/`} element={<Landing />} />
                <Route path={`${prefix}/promociones`} element={<Promociones />} />
                <Route path={`${prefix}/login`} element={<Login />} />
                <Route path={`${prefix}/dashboard`} element={<Dashboard />} />
                <Route path={`${prefix}/chat`} element={<Chat />} />
                <Route path={`${prefix}/tools`} element={<Tools />} />
                <Route path={`${prefix}/groups`} element={<Groups />} />
                <Route path={`${prefix}/groups/join/:groupId`} element={<GroupJoin />} />
                <Route path={`${prefix}/sos`} element={<SOSPage />} />
                <Route path={`${prefix}/feedback`} element={<Feedback />} />
              </React.Fragment>
            ))}

            {/* Panel de staff: sin prefijo de agencia — un solo login compartido,
                la marca se resuelve por la agencia del usuario logueado. */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/settings" element={<AgencySettings />} />
            <Route path="/master" element={<MasterAgencies />} />
            <Route path="/master/reportes" element={<MasterReports />} />
            <Route path="/master/personalizacion" element={<MasterPreferences />} />
            <Route path="/master/codigos" element={<MasterPromoCodes />} />
            <Route path="/admin/plan" element={<AgencyBilling />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SettingsProvider>
      </BrowserRouter>
    </div>
  );
}
